"use client";

import posthog from "posthog-js";
import type { SignupAttributionEventProperties } from "../../shared/signupAttribution";

/*
PostHog app funnel events:
- signup_completed: Clerk user was bootstrapped for the first time.
- device_trial_blocked: Trial was blocked because this device already used one.
- teaching_profile_completed: Teacher and school profile was completed for the first time.
- lesson_upload_started: First lesson screenshot upload was started.
- first_lesson_activation_viewed: The focused first-lesson activation screen was viewed.
- first_lesson_upload_cta_clicked: The first-lesson textbook upload control was opened.
- first_lesson_profile_prompt_viewed: The inline teacher-profile prompt was shown.
- lesson_plan_started: User began the focused lesson creation flow.
- lesson_source_selected: User chose upload or the bundled sample.
- lesson_image_uploaded: One safe upload batch completed.
- lesson_step_viewed: One of the three activation phases became visible.
- lesson_step_completed: User validly completed one activation phase.
- lesson_generation_started: A real lesson generation request began.
- lesson_plan_generated: A real lesson plan preview returned successfully.
- lesson_generation_failed: Generation failed with a normalized safe category.
- lesson_plan_downloaded: An accepted lesson was exported.
- lesson_plan_edited: A lesson preview or accepted lesson was edited.
- first_resource_generated: Server-side first accepted lesson milestone.
- generation_abandoned: User left an in-progress creation before generation.
- lesson_generate_clicked: Lesson preview generation was requested.
- lesson_generate_succeeded: Lesson preview generation returned successfully.
- lesson_generate_failed: Lesson preview generation failed with a sanitized error type.
- lesson_accept_clicked: User tried to accept a lesson preview.
- lesson_accepted: Lesson preview was accepted and saved.
- lesson_accept_blocked: Lesson acceptance hit the lesson paywall.
- lesson_discarded: User discarded an unaccepted lesson preview.
- test_analyse_clicked: Test analysis was requested.
- test_analyse_succeeded: Test analysis completed successfully.
- test_analyse_failed: Test analysis failed with a sanitized error type.
- test_generate_clicked: Test generation was requested.
- test_generate_succeeded: Test generation completed successfully.
- test_generate_failed: Test generation failed with a sanitized error type.
- test_accepted: Generated test was accepted and saved.
- test_pro_gate_blocked: Tests were blocked by the Pro/trial gate.
- audio_extract_clicked: Audio text extraction was requested.
- audio_extract_succeeded: Audio text extraction completed successfully.
- audio_extract_failed: Audio text extraction failed with a sanitized error type.
- audio_generate_clicked: MP3 generation was requested.
- audio_generate_succeeded: MP3 generation completed successfully.
- audio_generate_failed: MP3 generation failed with a sanitized error type.
- audio_pro_gate_blocked: Audio was blocked by the Pro/trial gate.
- audio_creation_started through audio_downloaded: Privacy-safe three-phase Audio studio funnel.
- template_extract_clicked: Personal template extraction was requested.
- template_extract_succeeded: Personal template extraction completed successfully.
- template_extract_failed: Personal template extraction failed with a sanitized error type.
- template_pro_gate_blocked: Personal templates were blocked by the Pro/trial gate.
- buy_page_viewed: Pricing/purchase page was viewed.
- purchase_plan_selected: A subscription or PAYG purchase option was selected.
- purchase_bank_selected: A transfer bank was selected.
- purchase_proof_uploaded: Payment proof upload completed.
- purchase_request_submitted: Payment request was submitted.
- purchase_request_failed: Payment request submission failed with a sanitized error type.
- zero_credits_modal_shown: Lesson paywall modal became visible.
- pro_lock_overlay_shown: A Pro-gated feature lock overlay became visible.
- history_page_viewed: History page was viewed.
- settings_page_viewed: Settings page was viewed.
- lesson_rated: A saved lesson was rated, without sending feedback text.
- marketing_cta_clicked: Public marketing CTA was clicked, without user content.
*/

export const ANALYTICS_EVENTS = {
  signupCompleted: "signup_completed",
  deviceTrialBlocked: "device_trial_blocked",
  teachingProfileCompleted: "teaching_profile_completed",
  lessonUploadStarted: "lesson_upload_started",
  firstLessonActivationViewed: "first_lesson_activation_viewed",
  firstLessonUploadCtaClicked: "first_lesson_upload_cta_clicked",
  firstLessonProfilePromptViewed: "first_lesson_profile_prompt_viewed",
  lessonPlanStarted: "lesson_plan_started",
  lessonSourceSelected: "lesson_source_selected",
  lessonImageUploaded: "lesson_image_uploaded",
  lessonStepViewed: "lesson_step_viewed",
  lessonStepCompleted: "lesson_step_completed",
  lessonGenerationStarted: "lesson_generation_started",
  lessonPlanGenerated: "lesson_plan_generated",
  lessonGenerationFailed: "lesson_generation_failed",
  lessonPlanDownloaded: "lesson_plan_downloaded",
  lessonPlanEdited: "lesson_plan_edited",
  firstResourceGenerated: "first_resource_generated",
  generationAbandoned: "generation_abandoned",
  lessonGenerateClicked: "lesson_generate_clicked",
  lessonGenerateSucceeded: "lesson_generate_succeeded",
  lessonGenerateFailed: "lesson_generate_failed",
  lessonAcceptClicked: "lesson_accept_clicked",
  lessonAccepted: "lesson_accepted",
  lessonAcceptBlocked: "lesson_accept_blocked",
  lessonDiscarded: "lesson_discarded",
  testAnalyseClicked: "test_analyse_clicked",
  testAnalyseSucceeded: "test_analyse_succeeded",
  testAnalyseFailed: "test_analyse_failed",
  testGenerateClicked: "test_generate_clicked",
  testGenerateSucceeded: "test_generate_succeeded",
  testGenerateFailed: "test_generate_failed",
  testAccepted: "test_accepted",
  testProGateBlocked: "test_pro_gate_blocked",
  testsPageViewed: "tests_page_viewed",
  testCreationStarted: "test_creation_started",
  testSourceSelected: "test_source_selected",
  testFileUploaded: "test_file_uploaded",
  testStepViewed: "test_step_viewed",
  testStepCompleted: "test_step_completed",
  testTypeSelected: "test_type_selected",
  testSetupCompleted: "test_setup_completed",
  testGenerationStarted: "test_generation_started",
  testGenerated: "test_generated",
  testGenerationFailed: "test_generation_failed",
  testPreviewViewed: "test_preview_viewed",
  testEdited: "test_edited",
  testSectionRegenerated: "test_section_regenerated",
  testDownloaded: "test_downloaded",
  testAnswerKeyDownloaded: "test_answer_key_downloaded",
  testCreationAbandoned: "test_creation_abandoned",
  audioExtractClicked: "audio_extract_clicked",
  audioExtractSucceeded: "audio_extract_succeeded",
  audioExtractFailed: "audio_extract_failed",
  audioGenerateClicked: "audio_generate_clicked",
  audioGenerateSucceeded: "audio_generate_succeeded",
  audioGenerateFailed: "audio_generate_failed",
  audioProGateBlocked: "audio_pro_gate_blocked",
  audioCreationStarted: "audio_creation_started",
  audioSourceSelected: "audio_source_selected",
  audioFileUploaded: "audio_file_uploaded",
  audioTextPasted: "audio_text_pasted",
  audioExtractionStarted: "audio_extraction_started",
  audioExtractionCompleted: "audio_extraction_completed",
  audioExtractionFailed: "audio_extraction_failed",
  audioStepViewed: "audio_step_viewed",
  audioStepCompleted: "audio_step_completed",
  audioScriptEdited: "audio_script_edited",
  audioSpeakerDetected: "audio_speaker_detected",
  audioSpeakerChanged: "audio_speaker_changed",
  audioVoicePreviewed: "audio_voice_previewed",
  audioVoiceSelected: "audio_voice_selected",
  audioSettingsChanged: "audio_settings_changed",
  audioSamplePlayed: "audio_sample_played",
  audioGenerationStarted: "audio_generation_started",
  audioGenerated: "audio_generated",
  audioGenerationFailed: "audio_generation_failed",
  audioPlayed: "audio_played",
  audioDownloaded: "audio_downloaded",
  audioRegenerationStarted: "audio_regeneration_started",
  audioCreationAbandoned: "audio_creation_abandoned",
  templateExtractClicked: "template_extract_clicked",
  templateExtractSucceeded: "template_extract_succeeded",
  templateExtractFailed: "template_extract_failed",
  templateProGateBlocked: "template_pro_gate_blocked",
  templatesPageViewed: "templates_page_viewed",
  templateCreationStarted: "template_creation_started",
  templateFileSelected: "template_file_selected",
  templateFileUploaded: "template_file_uploaded",
  templateAnalysisStarted: "template_analysis_started",
  templateAnalysisCompleted: "template_analysis_completed",
  templateAnalysisFailed: "template_analysis_failed",
  templateAnalysisReviewed: "template_analysis_reviewed",
  templateAnalysisCorrected: "template_analysis_corrected",
  templateScopeSelected: "template_scope_selected",
  templateDefaultSelected: "template_default_selected",
  templateCreated: "template_created",
  templateCreationFailed: "template_creation_failed",
  templateViewed: "template_viewed",
  templateRenamed: "template_renamed",
  templateScopeUpdated: "template_scope_updated",
  templateDefaultSet: "template_default_set",
  templateSourceReplaceStarted: "template_source_replace_started",
  templateReanalysisStarted: "template_reanalysis_started",
  templateReanalysisCompleted: "template_reanalysis_completed",
  templateUsedForGeneration: "template_used_for_generation",
  templateDeleted: "template_deleted",
  templateCreationAbandoned: "template_creation_abandoned",
  buyPageViewed: "buy_page_viewed",
  purchasePlanSelected: "purchase_plan_selected",
  purchaseBankSelected: "purchase_bank_selected",
  purchaseProofUploaded: "purchase_proof_uploaded",
  purchaseRequestSubmitted: "purchase_request_submitted",
  purchaseRequestFailed: "purchase_request_failed",
  billingPageViewed: "billing_page_viewed",
  billingSectionViewed: "billing_section_viewed",
  planComparisonViewed: "plan_comparison_viewed",
  billingPeriodChanged: "billing_period_changed",
  planSelected: "plan_selected",
  renewalStarted: "renewal_started",
  upgradeStarted: "upgrade_started",
  downgradeExplored: "downgrade_explored",
  creditQuantityChanged: "credit_quantity_changed",
  creditPurchaseStarted: "credit_purchase_started",
  purchaseReviewViewed: "purchase_review_viewed",
  bankTransferInstructionsViewed: "bank_transfer_instructions_viewed",
  paymentBankSelected: "payment_bank_selected",
  paymentProofUploadStarted: "payment_proof_upload_started",
  paymentProofUploaded: "payment_proof_uploaded",
  paymentProofUploadFailed: "payment_proof_upload_failed",
  paymentRequestSubmitted: "payment_request_submitted",
  paymentRequestSubmitFailed: "payment_request_submit_failed",
  paymentRequestViewed: "payment_request_viewed",
  billingHelpClicked: "billing_help_clicked",
  billingFaqOpened: "billing_faq_opened",
  billingEntryPointUsed: "billing_entry_point_used",
  promoViewed: "promo_viewed",
  promoApplied: "promo_applied",
  zeroCreditsModalShown: "zero_credits_modal_shown",
  proLockOverlayShown: "pro_lock_overlay_shown",
  historyPageViewed: "history_page_viewed",
  resourceLibraryViewed: "resource_library_viewed",
  resourceSearchUsed: "resource_search_used",
  resourceTypeFiltered: "resource_type_filtered",
  resourceFilterApplied: "resource_filter_applied",
  resourceFiltersCleared: "resource_filters_cleared",
  resourceSortChanged: "resource_sort_changed",
  resourcePageLoaded: "resource_page_loaded",
  resourceOpened: "resource_opened",
  resourcePrimaryActionClicked: "resource_primary_action_clicked",
  resourceDownloaded: "resource_downloaded",
  resourceFavorited: "resource_favorited",
  resourceUnfavorited: "resource_unfavorited",
  resourceRenamed: "resource_renamed",
  resourceDuplicated: "resource_duplicated",
  resourceDeleteStarted: "resource_delete_started",
  resourceDeleted: "resource_deleted",
  resourceDeleteFailed: "resource_delete_failed",
  unfinishedResourceResumed: "unfinished_resource_resumed",
  failedResourceRetryStarted: "failed_resource_retry_started",
  resourceCreateNewClicked: "resource_create_new_clicked",
  resourceCrossFeatureStarted: "resource_cross_feature_started",
  resourceAudioPlayed: "resource_audio_played",
  settingsPageViewed: "settings_page_viewed",
  lessonRated: "lesson_rated",
  marketingCtaClicked: "marketing_cta_clicked",
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsProperty = string | number | boolean | null;
type EmptyProperties = Record<string, never>;

export type MarketingCtaId =
  | "hero_primary"
  | "hero_secondary_how_it_works"
  | "proof_section"
  | "why_darsi"
  | "pricing_trial_banner"
  | "pricing_starter"
  | "pricing_pro"
  | "pricing_payg_per_lesson"
  | "pricing_credit_packs"
  | "showcase_preview_modal"
  | "faq"
  | "mobile_sticky"
  | "final";

export type AnalyticsEventProperties = {
  signup_completed: SignupAttributionEventProperties & {
    isNewUser: boolean;
    trialStarted: boolean;
    deviceTrialBlocked: boolean;
  };
  device_trial_blocked: EmptyProperties;
  teaching_profile_completed: { isFirstCompletion: boolean };
  lesson_upload_started: { imageCount: number };
  first_lesson_activation_viewed: {
    trial_status: "active" | "ended" | "device_blocked" | "not_applicable";
    profile_complete: boolean;
  };
  first_lesson_upload_cta_clicked: EmptyProperties;
  first_lesson_profile_prompt_viewed: EmptyProperties;
  lesson_plan_started: { is_first_generation: boolean };
  lesson_source_selected: {
    source: "upload" | "sample";
    is_first_generation: boolean;
  };
  lesson_image_uploaded: {
    file_count: number;
    file_types: string;
    source: "upload";
    is_first_generation: boolean;
  };
  lesson_step_viewed: {
    step: "lesson" | "class_preferences" | "generate";
    source: "upload" | "sample" | null;
    is_first_generation: boolean;
  };
  lesson_step_completed: {
    step: "lesson" | "class_preferences" | "generate";
    source: "upload" | "sample" | null;
    is_first_generation: boolean;
  };
  lesson_generation_started: {
    source: "upload" | "sample";
    lesson_type: string;
    grade: string;
    plan_tier: string;
    is_first_generation: boolean;
  };
  lesson_plan_generated: {
    source: "upload" | "sample";
    generation_duration_ms: number;
    lesson_type: string;
    is_first_generation: boolean;
  };
  lesson_generation_failed: {
    source: "upload" | "sample";
    error_category: string;
    is_first_generation: boolean;
  };
  lesson_plan_downloaded: {
    export_format: "docx" | "print";
    is_first_generation: boolean;
  };
  lesson_plan_edited: { is_first_generation: boolean };
  first_resource_generated: { resource_type: "lesson_plan" };
  generation_abandoned: {
    step: "lesson" | "class_preferences" | "generate";
    source: "upload" | "sample";
    is_first_generation: boolean;
  };
  lesson_generate_clicked: {
    lessonType: string;
    grade: string;
    duration: string;
    templateId: string;
    hasGuideImages: boolean;
    isRetry: boolean;
    retryNumber: number;
  };
  lesson_generate_succeeded: {
    modelUsed: string;
    lessonType: string;
    templateId: string;
    isRetry: boolean;
  };
  lesson_generate_failed: { errorType: string; lessonType: string };
  lesson_accept_clicked: EmptyProperties;
  lesson_accepted: {
    lessonType: string;
    templateId: string;
    billingPath: "subscription" | "trial" | "payg";
  };
  lesson_accept_blocked: EmptyProperties;
  lesson_discarded: EmptyProperties;
  test_analyse_clicked: { testType: string; grade: string; imageCount: number };
  test_analyse_succeeded: { testType: string; grade: string };
  test_analyse_failed: { errorType: string; testType: string };
  test_generate_clicked: { testType: string; grade: string };
  test_generate_succeeded: { testType: string; grade: string };
  test_generate_failed: { errorType: string; testType: string };
  test_accepted: EmptyProperties;
  test_pro_gate_blocked: EmptyProperties;
  tests_page_viewed: EmptyProperties;
  test_creation_started: { is_first_test: boolean; plan_tier: string };
  test_source_selected: {
    source: "upload" | "sample" | "existing_lesson";
    is_first_test: boolean;
  };
  test_file_uploaded: {
    source: "upload";
    file_count: number;
    file_types: string;
    is_first_test: boolean;
  };
  test_step_viewed: {
    step: "lesson_content" | "test_setup" | "generate";
    source: "upload" | "sample" | "existing_lesson" | null;
    is_first_test: boolean;
  };
  test_step_completed: {
    step: "lesson_content" | "test_setup" | "generate";
    source: "upload" | "sample" | "existing_lesson";
    is_first_test: boolean;
  };
  test_type_selected: { test_type: "quiz" | "global_test" };
  test_setup_completed: {
    source: "upload" | "sample" | "existing_lesson";
    test_type: "quiz" | "global_test";
    grade_level: string;
    difficulty: "easy" | "medium" | "hard";
    duration_minutes: number;
    section_count: number;
    file_count: number;
    is_first_test: boolean;
  };
  test_generation_started: {
    source: "upload" | "sample" | "existing_lesson";
    test_type: "quiz" | "global_test";
    grade_level: string;
    difficulty: "easy" | "medium" | "hard";
    duration_minutes: number;
    section_count: number;
    file_count: number;
    is_first_test: boolean;
    plan_tier: string;
  };
  test_generated: {
    source: "upload" | "sample" | "existing_lesson";
    test_type: "quiz" | "global_test";
    grade_level: string;
    generation_duration_ms: number;
    is_first_test: boolean;
  };
  test_generation_failed: {
    source: "upload" | "sample" | "existing_lesson";
    test_type: "quiz" | "global_test";
    error_category: string;
    is_first_test: boolean;
  };
  test_preview_viewed: {
    test_type: "quiz" | "global_test";
    status: "pending_acceptance" | "accepted";
  };
  test_edited: { scope: "section" | "task" };
  test_section_regenerated: { scope: "section" | "task" };
  test_downloaded: {
    test_type: "quiz" | "global_test";
    format: "print" | "doc";
  };
  test_answer_key_downloaded: {
    test_type: "quiz" | "global_test";
    format: "print" | "doc";
  };
  test_creation_abandoned: {
    step: "lesson_content" | "test_setup" | "generate";
    source: "upload" | "sample" | "existing_lesson";
    is_first_test: boolean;
  };
  audio_extract_clicked: { imageCount: number };
  audio_extract_succeeded: { extractionType: string; segmentCount: number };
  audio_extract_failed: { errorType: string };
  audio_generate_clicked: { extractionType: string; segmentCount: number };
  audio_generate_succeeded: { extractionType: string; segmentCount: number };
  audio_generate_failed: { errorType: string; extractionType: string };
  audio_pro_gate_blocked: EmptyProperties;
  audio_creation_started: { is_first_audio: boolean; plan_tier: string };
  audio_source_selected: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    is_first_audio: boolean;
  };
  audio_file_uploaded: {
    source: "upload";
    file_count: number;
    file_types: string;
    is_first_audio: boolean;
  };
  audio_text_pasted: {
    source: "pasted_text";
    character_count: number;
    estimated_duration_seconds: number;
    is_first_audio: boolean;
  };
  audio_extraction_started: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    file_count: number;
    is_first_audio: boolean;
  };
  audio_extraction_completed: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    line_count: number;
    speaker_count: number;
    is_first_audio: boolean;
  };
  audio_extraction_failed: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    error_category: string;
    is_first_audio: boolean;
  };
  audio_step_viewed: {
    step: "add_content" | "review_script" | "generate";
    source: "upload" | "pasted_text" | "existing_content" | "sample" | null;
    is_first_audio: boolean;
  };
  audio_step_completed: {
    step: "add_content" | "review_script" | "generate";
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    line_count: number;
    speaker_count: number;
    is_first_audio: boolean;
  };
  audio_script_edited: {
    edit_type: "text" | "add_line" | "remove_line" | "reorder_line" | "undo";
    line_count: number;
    speaker_count: number;
  };
  audio_speaker_detected: { speaker_count: number; source: string };
  audio_speaker_changed: {
    change_type: "rename" | "assign_line" | "add" | "narration";
    speaker_count: number;
  };
  audio_voice_previewed: { voice_name: string; locale: string };
  audio_voice_selected: { speaker_count: number; voice_count: number; locale: string };
  audio_settings_changed: { speed: "slow" | "normal" | "fast"; locale: string };
  audio_sample_played: { speaker_count: number; locale: string };
  audio_generation_started: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    line_count: number;
    speaker_count: number;
    voice_count: number;
    speed: "slow" | "normal" | "fast";
    locale: string;
    estimated_duration_seconds: number;
    is_first_audio: boolean;
    plan_tier: string;
  };
  audio_generated: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    generation_duration_ms: number;
    line_count: number;
    speaker_count: number;
    is_first_audio: boolean;
  };
  audio_generation_failed: {
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    error_category: string;
    is_first_audio: boolean;
  };
  audio_played: { location: "example" | "result" | "history" };
  audio_downloaded: { source: string; is_first_audio: boolean };
  audio_regeneration_started: { source: string; line_count: number };
  audio_creation_abandoned: {
    step: "add_content" | "review_script" | "generate";
    source: "upload" | "pasted_text" | "existing_content" | "sample";
    is_first_audio: boolean;
  };
  template_extract_clicked: {
    scope: string;
    sourceType: "image" | "pdf" | "docx";
    fileCount: number;
  };
  template_extract_succeeded: { scope: string; sourceType: "image" | "pdf" | "docx" };
  template_extract_failed: { errorType: string; scope: string; sourceType: "image" | "pdf" | "docx" };
  template_pro_gate_blocked: EmptyProperties;
  templates_page_viewed: { template_count: number; plan_tier: string };
  template_creation_started: { is_first_template: boolean; mode: "create" | "replace" };
  template_file_selected: { file_type: "image" | "pdf" | "docx"; page_count: number };
  template_file_uploaded: { file_type: "image" | "pdf" | "docx"; page_count: number };
  template_analysis_started: { file_type: "image" | "pdf" | "docx"; page_count: number };
  template_analysis_completed: {
    file_type: "image" | "pdf" | "docx";
    page_count: number;
    detected_stage_count: number;
    detected_lesson_type: string | null;
    analysis_duration_ms: number;
  };
  template_analysis_failed: { file_type: "image" | "pdf" | "docx"; error_category: string };
  template_analysis_reviewed: { detected_stage_count: number; detected_lesson_type: string | null };
  template_analysis_corrected: { correction_type: "stage_name" | "stage_order" | "stage_removed" | "timing" | "lesson_type" | "summary" };
  template_scope_selected: { scope: string };
  template_default_selected: { is_default: boolean };
  template_created: { scope: string; is_default: boolean; detected_stage_count: number; is_first_template: boolean };
  template_creation_failed: { error_category: string; step: "upload" | "review" | "configure" | "save" };
  template_viewed: { scope: string; is_default: boolean; usage_count: number };
  template_renamed: EmptyProperties;
  template_scope_updated: { scope: string };
  template_default_set: { scope: string };
  template_source_replace_started: EmptyProperties;
  template_reanalysis_started: { file_type: "image" | "pdf" | "docx" };
  template_reanalysis_completed: { detected_stage_count: number };
  template_used_for_generation: { scope: string; is_default: boolean };
  template_deleted: { was_default: boolean; usage_count: number };
  template_creation_abandoned: { step: "upload" | "review" | "configure" | "save"; had_analysis: boolean };
  buy_page_viewed: { currentBillingState: string };
  purchase_plan_selected: {
    purchaseKind: "subscription" | "payg";
    plan: "starter" | "pro" | null;
    billingCycle: "semester" | "yearly" | null;
    credits: number | null;
    amount: number;
  };
  purchase_bank_selected: { bankId: string };
  purchase_proof_uploaded: EmptyProperties;
  purchase_request_submitted: {
    purchaseKind: "subscription" | "payg";
    amount: number;
  };
  purchase_request_failed: { errorType: string };
  billing_page_viewed: { current_access_category: string; entry_point: string };
  billing_section_viewed: { billing_section: "overview" | "plans" | "credits" | "payments"; current_access_category: string };
  plan_comparison_viewed: { current_plan: string };
  billing_period_changed: { billing_cycle: "semester" | "yearly" };
  plan_selected: { selected_plan: "starter" | "pro"; billing_cycle: "semester" | "yearly"; current_plan: string };
  renewal_started: { selected_plan: "starter" | "pro"; billing_cycle: "semester" | "yearly"; days_until_expiration_bucket: string };
  upgrade_started: { selected_plan: "pro"; billing_cycle: "semester" | "yearly"; current_plan: string };
  downgrade_explored: { current_plan: "pro"; selected_plan: "starter" };
  credit_quantity_changed: { credit_quantity_bucket: string };
  credit_purchase_started: { credit_quantity_bucket: string };
  purchase_review_viewed: { purchase_type: "subscription" | "payg"; selected_plan: string; billing_cycle: string };
  bank_transfer_instructions_viewed: { purchase_type: "subscription" | "payg" };
  payment_bank_selected: { bank: "attijariwafa" | "cih" };
  payment_proof_upload_started: { file_type: "image" | "pdf" };
  payment_proof_uploaded: { file_type: "image" | "pdf" };
  payment_proof_upload_failed: { error_category: string };
  payment_request_submitted: {
    purchase_type: "subscription" | "payg";
    campaign?: string;
    promo_code?: string;
    original_amount_mad?: number;
    discount_amount_mad?: number;
    final_amount_mad?: number;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  };
  promo_viewed: {
    campaign: string;
    promo_code: string;
    discount_percent: number;
    source: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  };
  promo_applied: {
    campaign: string;
    promo_code: string;
    discount_percent: number;
    selected_plan: "starter" | "pro";
    billing_cycle: "semester" | "yearly";
    purchase_kind: "subscription";
    original_amount_mad: number;
    discount_amount_mad: number;
    final_amount_mad: number;
    source: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  };
  payment_request_submit_failed: { error_category: string };
  payment_request_viewed: { payment_status: "pending" | "approved" | "rejected" };
  billing_help_clicked: { billing_section: "overview" | "plans" | "credits" | "payments" };
  billing_faq_opened: { faq_key: string };
  billing_entry_point_used: { entry_point: string; billing_section: "overview" | "plans" | "credits" | "payments" };
  zero_credits_modal_shown: {
    trigger: string;
    hadActiveTrialBefore: boolean;
    reason?: "generic" | "trial_ended" | "device_blocked";
    hasPendingRequest?: boolean;
  };
  pro_lock_overlay_shown: { feature: "tests" | "audio" | "templates" };
  history_page_viewed: EmptyProperties;
  resource_library_viewed: { result_count: number };
  resource_search_used: { result_count: number };
  resource_type_filtered: { resource_type: string; result_count: number };
  resource_filter_applied: { filter_type: string; resource_type: string };
  resource_filters_cleared: { resource_type: string };
  resource_sort_changed: { sort_option: string; resource_type: string };
  resource_page_loaded: { page_number: number; result_count: number; resource_type: string };
  resource_opened: { resource_type: string; resource_subtype: string; status: string; grade_level: string; is_favorite: boolean };
  resource_primary_action_clicked: { resource_type: string; resource_subtype: string; status: string };
  resource_downloaded: { resource_type: string; resource_subtype: string; status: string };
  resource_favorited: { resource_type: string; resource_subtype: string; status: string; is_favorite: true };
  resource_unfavorited: { resource_type: string; resource_subtype: string; status: string; is_favorite: false };
  resource_renamed: { resource_type: string; resource_subtype: string; status: string };
  resource_duplicated: { resource_type: string; resource_subtype: string; status: string };
  resource_delete_started: { resource_type: string; resource_subtype: string; status: string };
  resource_deleted: { resource_type: string; resource_subtype: string; status: string };
  resource_delete_failed: { resource_type: string; resource_subtype: string; status: string; error_category: string };
  unfinished_resource_resumed: { resource_type: string; resource_subtype: string; status: string };
  failed_resource_retry_started: { resource_type: string; resource_subtype: string; status: string };
  resource_create_new_clicked: { resource_type: string; plan_tier: string };
  resource_cross_feature_started: { resource_type: string; resource_subtype: string; cross_feature_destination: string };
  resource_audio_played: { resource_type: "audio"; resource_subtype: string; status: string };
  settings_page_viewed: EmptyProperties;
  lesson_rated: { rating: number; hasFeedback: boolean };
  marketing_cta_clicked: {
    ctaId: MarketingCtaId;
    ctaLabel: string;
    href: string;
  };
};

function isClientReady(): boolean {
  return typeof window !== "undefined";
}

function analyticsEnabled(): boolean {
  return Boolean(
    isClientReady() &&
      process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN &&
      process.env.NEXT_PUBLIC_POSTHOG_HOST,
  );
}

function cleanProperties(
  properties: Record<string, AnalyticsProperty | undefined>,
): Record<string, AnalyticsProperty> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  ) as Record<string, AnalyticsProperty>;
}

export function track<EventName extends AnalyticsEventName>(
  event: EventName,
  properties: AnalyticsEventProperties[EventName] = {} as AnalyticsEventProperties[EventName],
): void {
  if (!analyticsEnabled()) {
    return;
  }
  try {
    posthog.capture(
      event,
      cleanProperties(properties as Record<string, AnalyticsProperty | undefined>),
    );
  } catch {
    // Analytics must never affect product behavior.
  }
}

export function identifyAnalyticsUser(distinctId: string): void {
  if (!analyticsEnabled()) {
    return;
  }
  try {
    posthog.identify(distinctId);
  } catch {
    // Analytics must never affect signup or authentication behavior.
  }
}

export function analyticsErrorType(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (message.includes("SUBSCRIPTION_REQUIRED")) return "SUBSCRIPTION_REQUIRED";
  if (/image.*large|too large|max\s*\d+\s*mb/i.test(message)) return "IMAGE_TOO_LARGE";
  if (/not authenticated|sign in|auth/i.test(message)) return "AUTHENTICATION_ERROR";
  if (/insufficient|credit|pro plan|trial/i.test(message)) return "ENTITLEMENT_ERROR";
  if (/timeout|timed out|abort/i.test(message)) return "TIMEOUT";
  if (/storage|audio.*unavailable|not found/i.test(message)) return "STORAGE_ERROR";
  if (/provider|gateway|speech|generation failed/i.test(message)) return "PROVIDER_ERROR";
  if (/invalid|required|missing|validation|must be|choose|upload/i.test(message)) {
    return "VALIDATION_ERROR";
  }
  if (/network|fetch|connection/i.test(message)) return "NETWORK_ERROR";
  return "UNKNOWN";
}
